import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import NewFeatures from './NewFeatures';
import {
  BookOpen,
  Calendar,
  Users,
  BarChart2,
  MessageSquare,
  FolderOpen,
  Repeat,
  Bot,
  GraduationCap,
  Zap,
  Settings,
  ChevronRight,
  Search,
  ArrowRight,
  FileText,
  Layout,
  Cloud
} from 'lucide-react';

// Documentation sections
const sections = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: BookOpen,
    items: [
      { id: 'welcome', title: 'Welcome Overview', path: '/docs' },
      { id: 'new-features', title: 'New Features', path: '/docs/new-features' },
      { id: 'quickstart', title: 'Quick Start Guide', path: '/docs/quickstart' },
      { id: 'dashboard', title: 'Dashboard Overview', path: '/docs/dashboard' }
    ]
  },
  {
    id: 'features',
    title: 'Features',
    icon: Layout,
    items: [
      { id: 'calendar', title: 'Calendar & Scheduling', path: '/docs/calendar' },
      { id: 'analytics', title: 'Analytics & Performance', path: '/docs/analytics' },
      { id: 'messaging', title: 'Messaging System', path: '/docs/messaging' },
      { id: 'contacts', title: 'Contact Management', path: '/docs/contacts' },
      { id: 'library', title: 'Content Vault', path: '/docs/library' }
    ]
  },
  {
    id: 'ai-features',
    title: 'AI Features',
    icon: Bot,
    items: [
      { id: 'creator-assistant', title: 'Creator Assistant', path: '/docs/creator-assistant' },
      { id: 'content-repurposing', title: 'Content Repurposing', path: '/docs/content-repurposing' },
      { id: 'automated-posts', title: 'Automated Posts', path: '/docs/automated-posts' }
    ]
  },
  {
    id: 'integrations',
    title: 'Integrations',
    icon: Cloud,
    items: [
      { id: 'social-platforms', title: 'Social Media Platforms', path: '/docs/social-platforms' },
      { id: 'webhooks', title: 'Webhooks & APIs', path: '/docs/webhooks' }
    ]
  }
];

// Welcome content component
const WelcomeContent = () => (
  <div className="prose dark:prose-invert max-w-none">
    <h1>Welcome to Serge Documentation</h1>
    <p>
      Welcome to the comprehensive documentation for Serge, your all-in-one content creation and management platform.
      This guide will help you understand and make the most of our features and tools.
    </p>

    <h2>What is Serge?</h2>
    <p>
      Serge is a powerful platform designed for content creators, social media managers, and digital marketers.
      It combines content management, scheduling, analytics, and AI-powered assistance to streamline your content
      creation workflow.
    </p>

    <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-6 my-8">
      <h3 className="text-lg font-semibold text-indigo-900 dark:text-indigo-100 mt-0">Key Features</h3>
      <ul className="mt-4 space-y-3">
        <li className="flex items-start">
          <Calendar className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3 flex-shrink-0" />
          <div>
            <strong>Content Calendar</strong>
            <p className="mt-1">Schedule and manage your content across multiple platforms</p>
          </div>
        </li>
        <li className="flex items-start">
          <BarChart2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3 flex-shrink-0" />
          <div>
            <strong>Analytics Dashboard</strong>
            <p className="mt-1">Track performance metrics and engagement across platforms</p>
          </div>
        </li>
        <li className="flex items-start">
          <Bot className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3 flex-shrink-0" />
          <div>
            <strong>AI-Powered Assistant</strong>
            <p className="mt-1">Get content suggestions and optimization tips</p>
          </div>
        </li>
        <li className="flex items-start">
          <Repeat className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-1 mr-3 flex-shrink-0" />
          <div>
            <strong>Content Repurposing</strong>
            <p className="mt-1">Automatically adapt content for different platforms</p>
          </div>
        </li>
      </ul>
    </div>

    <h2>Getting Started</h2>
    <p>
      To begin using Serge, follow these steps:
    </p>
    <ol>
      <li>Complete the onboarding quiz to customize your experience</li>
      <li>Connect your social media accounts</li>
      <li>Set up your content calendar</li>
      <li>Explore the AI-powered features</li>
    </ol>

    <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 my-8 text-white">
      <h3 className="text-white mt-0 mb-4">Need Help?</h3>
      <p className="text-indigo-100 mb-4">
        Our support team is here to help you make the most of Serge. Don't hesitate to reach out if you have any questions.
      </p>
      <div className="flex space-x-4">
        <Link
          to="/creator-assistant"
          className="inline-flex items-center px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors"
        >
          <Bot className="h-4 w-4 mr-2" />
          Ask AI Assistant
        </Link>
        <a
          href="mailto:support@serge.com"
          className="inline-flex items-center px-4 py-2 bg-indigo-400/20 text-white rounded-lg hover:bg-indigo-400/30 transition-colors"
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          Contact Support
        </a>
      </div>
    </div>

    <h2>Next Steps</h2>
    <p>
      Explore our documentation sections to learn more about specific features and how to use them effectively.
      We recommend starting with the Quick Start Guide for a comprehensive overview of the platform.
    </p>
  </div>
);

const Documentation = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  return (
    <div className="min-h-[calc(100vh-8rem)]">
      <div className="flex space-x-8">
        {/* Sidebar Navigation */}
        <div className="w-64 flex-shrink-0">
          <div className="sticky top-24">
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search docs..."
                  className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-dark-700 border-0 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                />
              </div>
            </div>

            <nav className="space-y-8">
              {sections.map((section) => (
                <div key={section.id}>
                  <div className="flex items-center space-x-2 mb-2">
                    <section.icon className="h-5 w-5 text-gray-400" />
                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                  </div>
                  <ul className="space-y-2">
                    {section.items.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={item.path}
                          className={`flex items-center text-sm py-1.5 px-3 rounded-lg transition-colors ${
                            location.pathname === item.path
                              ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-dark-700'
                          }`}
                        >
                          <ChevronRight className={`h-4 w-4 mr-2 transition-transform ${
                            location.pathname === item.path ? 'rotate-90' : ''
                          }`} />
                          {item.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-8">
            <Routes>
              <Route index element={<WelcomeContent />} />
              <Route path="/new-features" element={<NewFeatures />} />
              {/* Add routes for other documentation pages */}
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;